import CreateNewFolderIcon from "@material-ui/icons/CreateNewFolder";
import PropTypes from "prop-types";
import React, { Fragment, useEffect, useState } from "react";
import { FoldersApi } from '../../../api/folders/folders';
import CustomButton from "../../../components/CustomButton/CustomButton";
import CustomConfirmationDialog from "../../../components/CustomConfirmationDialog/CustomConfirmationDialog";
import EditFolderForm from "../../../components/Forms/EditFolderForm";
import { useSnackbar } from "../../../data/SnackbarContext";
import * as Constants from "../../../data/StringConstants";

import analytics from "../../../utils/analytics";

/**
 * This component provides the ability for a user to create a new Folder. It's composed of a
 * <CustomButton> component, which toggles displaying the form used to create the Folder. Our
 * users *need* to provide a Title and select a Branding Set. The description is optional.
 * 
 * The actual form and validation is handled by the EditFolderForm component.
 * @param {Object} history The react-router history object.
 */
export default function CreateFolderDialogButton({ history, matchedComponentName }) {
	const [isOpen, setIsOpen] = useState(false);
	const { showSuccessSnackbar, showErrorSnackbar } = useSnackbar();

	let initialFormData = {
		name: "",
		description: "",
		folderId: 1,
		defaultBrandingSetId: 1,
		brandingSets: [{ id: 1, name: "Default" }]
	};

	const [formData, setFormData] = useState(initialFormData);

	// Called whenever the user submits the form to create a new Folder
	const onCreateSubmit = (data) => {
		analytics.logEvent(analytics.eventTypes.FOLDERS.CREATE_FOLDER_CLICKED, {
			smartView: matchedComponentName,
			closeOrCreate: "create"
		});

		const formSubmissionData = {
			name: data.name,
			description: data.description,
			defaultBrandingSetId: data.brandingSet
		};

		FoldersApi.createFolder(formSubmissionData)
			.then((response) => {
				setIsOpen(false);
				showSuccessSnackbar(Constants.SNACKBAR_MESSAGES.CREATE_FOLDER_SUCCESS);
				history.push(`#!/folders/${response.data.id}`);
			})
			.catch((error) => {
				setIsOpen(false);
				showErrorSnackbar(Constants.SNACKBAR_MESSAGES.CREATE_FOLDER_ERROR);
			});
	}

	const closeDialog = () => {
		analytics.logEvent(analytics.eventTypes.FOLDERS.CREATE_FOLDER_CLICKED, {
			smartView: matchedComponentName,
			closeOrCreate: "close"
		});
		setIsOpen(false);
	}

	// When our component mounts, we fetch the available Branding Sets and update the form State
	useEffect(() => {
		let isCancelled = false;
		FoldersApi.getCreateFolderContext()
			.then(response => {
				!isCancelled && setFormData(prevState => {
					return {
						...prevState,
						defaultBrandingSetId: response.data.brandingSets[0].id,
						brandingSets: response.data.brandingSets
					};
				});
			})
			.catch(error => {
				console.log(Constants.NO_CREATE_FOLDER_RESP_ERROR, error);
			});

		return () => (isCancelled = true);
	}, [])

	return (
		<Fragment>
			<CustomButton
				color="green"
				variant="contained"
				onClick={() => setIsOpen(true)}
				startIcon={<CreateNewFolderIcon />}
			>
				{Constants.EDIT_FOLDER_FORM.CREATE_BTN_LABEL}
			</CustomButton>
			<CustomConfirmationDialog
				onClose={closeDialog}
				open={isOpen}
				title={"Create Folder"}
			>
				<EditFolderForm
					data={formData}
					onSubmit={onCreateSubmit}
					onCancel={closeDialog}
					isCreate={true}
				/>
			</CustomConfirmationDialog>
		</Fragment>
	);
}

CreateFolderDialogButton.propTypes = {
	history: PropTypes.object,
	matchedComponentName: PropTypes.string
}